import React, {useEffect, useMemo } from "react";

import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import * as constants from "../../constants/authConstants";
import * as routeConstants from "../../constants/routeConstants";

import { login, logout } from "../../store/auth/auth-actions";
import { getMyProfile } from "../../store/auth/auth-actions";

import {getAbortSignal} from '../../util/signalHelper';

const TOKEN_REFRESH_INTERVAL_IN_MILLIS = process.env.REACT_APP_OAUTH_TOKEN_REFRESH_INTERVAL_IN_MILLIS;// ? process.env.REACT_APP_OAUTH_TOKEN_REFRESH_INTERVAL_IN_MILLIS : 10*60*60*1000;


//make sure you understand everything properly before editing this file
const Authentication = props => {
  const dispatch = useDispatch();
  const navigate = useNavigate(); 
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  const isAuthenticating = location.pathname === routeConstants.ROUTE_AUTH;
  
  let token = auth.token;
  let expiresAt = useMemo(() => auth.expiresAt ? new Date(auth.expiresAt) : null, [auth.expiresAt]);
  let callLogin = false;

 // const isExpired = expiresAt && !(expiresAt.getTime() - new Date().getTime());
  const isExpired = expiresAt && (expiresAt.getTime() - new Date().getTime()) < 0;

  useEffect(() => {
    console.count("Authentication -> useEffect login");
    if (!callLogin) return;
    if (isExpired) return;
    if (!token) return;
    console.count("Authentication -> useEffect login: dispatching");
    
    //About to expire -> refresh token to avoid token expires during session 
    //Example: token valid for 24h. User comes in on day one at 9:00. So token gets created with expire the next day at 9:00. 
    //Second day user comes in again, but earlier on the day that the first day: 8:55. Token still OK and expiration not refreshed. 
    //5 minutes later: user get's thrown out when trying to do something because token expired.
    //Proposal: token valid for 48h (=max) - refresh when accessing the '/auth' url(user clicks on admin->skillmatrix) and token validity less then 24h.
    //User is just coming in to the application so isn't really doing anything yet. Token get's refreshed so user can work for the day.
    console.log("Time to expire: ", (expiresAt.getTime() - new Date().getTime()), " Refresh when less then: ", TOKEN_REFRESH_INTERVAL_IN_MILLIS);
    if(TOKEN_REFRESH_INTERVAL_IN_MILLIS && expiresAt.getTime() - new Date().getTime() <= TOKEN_REFRESH_INTERVAL_IN_MILLIS) {
      console.log("Token almost expiring - Better to refresh token now");
      window.location.href= constants.LOGIN_URL;
      return;
    }

    dispatch(login(token, expiresAt));

  }, [dispatch, token, expiresAt, callLogin, isExpired]);

  useEffect(() => {
    const [signal, controller] = getAbortSignal();

    console.count("Authentication -> useEffect profile");
    if (!auth.token) return;
    if (isExpired) return;
    
    console.count("Authentication -> useEffect profile: getMyProfile");
    dispatch(getMyProfile(auth.token, signal));

    //cleanup functions appear to be a necessity nowadays for future react versions regarding improved performance called "reusable state"
    //https://reactjs.org/docs/strict-mode.html#ensuring-reusable-state
    return () => {
      console.count("Authentication -> cancel getMyProfile");
      controller.abort();
    }
  }, [dispatch, auth.token, isExpired]);

  useEffect(() => {
    console.log("Authentication -> useEffect expired");
    if (!isExpired) return;
    console.count("Authentication -> useEffect expired: logout");
    dispatch(logout());
  }, [dispatch, isExpired]);

  useEffect(() => {
    const redirectToLocation = window.localStorage.getItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN);

    if (redirectToLocation && auth.isLoggedIn && !isAuthenticating) {
      window.localStorage.removeItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN);
      console.log(`Authentication -> useEffect: redirecting to: ${redirectToLocation}`);
      navigate(redirectToLocation);
    } 
  }, [auth.isLoggedIn, isAuthenticating, navigate]);

  if (auth.doRedirectLogout) {
    console.log("Authentication -> directing to logout url");
    window.location.href = constants.LOGOUT_URL;
    return (<></>);
  }

  //default behaviour
  if (auth.isLoggedIn && !isAuthenticating) {    
    //load content, useEffect will handle redirect if set
    //console.log("Authentication -> user is logged in, route is not authenticating");
    return (props.children);
  }

  //Check for token and expiresIn in url parameters
  if (window.location.href.includes(constants.KEY_ACCESS_TOKEN_PREFIXED)) {
    //get token from url
    const urlParams = new URLSearchParams(window.location.hash.substring(1));
    token = urlParams.get(constants.KEY_ACCESS_TOKEN);

    //get expiredIn from url
    if (window.location.href.includes(constants.KEY_EXPIRES_IN)) {
      const expiresIn = urlParams.get(constants.KEY_EXPIRES_IN);
      expiresAt = new Date(new Date().getTime() + (1000 * expiresIn));
    }
  }

  //if there is no token and the path is /auth -> get token (=redirect to login url at Genesys)
  if (!token && isAuthenticating) {
    console.log("Authentication -> will direct to login url");
    window.location.href= constants.LOGIN_URL;
    return (<></>);
  }

/*
console.log("Token: ", token);
console.log("now: ", new Date().getTime());
console.log("expiresAt: ", expiresAt.getTime());
console.log("diff: ", (expiresAt.getTime() - new Date().getTime()));
console.log("false-y: ", !(expiresAt.getTime() - new Date().getTime()));
console.log("false-y 2: ", (expiresAt.getTime() - new Date().getTime()) < 0);
console.log("isExpired: ", isExpired);
console.log("isAuthenticating: ", isAuthenticating);
*/

  //if there is an expired token and the path is /auth -> get token (=redirect to login url at Genesys)
  if (token && isExpired && isAuthenticating) {
    console.log("Authentication -> expired token - will direct to login url");
    window.location.href= constants.LOGIN_URL;
    return (<></>);
  }

  //not being logged in will trigger log in in useEffect -> set redirect page
  if (!auth.isLoggedIn && !isAuthenticating) {
    window.localStorage.setItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN, location.pathname);
  }

  //make sure the useEffect logs you in after page render
  callLogin = true;

  //if you arrive here this means that the token is set.
  //The login useEffect will also be called
  //It is necessary to move away from the /auth endpoint -> go home
  if (isAuthenticating) {
    console.log("Authentication -> token is set, page is authenticating, move to default route");
    return (<Navigate replace to={routeConstants.ROUTE_DEFAULT} />);
  }
  
  //load children -> when logged out and no token is set in the local storage
  console.log("Authentication -> no token is set, user is logged out");
  return (props.children);
}

export default Authentication;